import { Market, MarketOutcome, MarketStatus, MarketType, Selection, SelectionOutcome, SelectionStatus, Side } from '../types/marketTypes';
import { Event } from '../types/types';
import { MetaMarketDTO } from '../types/api';
import { apiService } from './api';
import { ENDPOINTS } from '../config/api';
import { featureService } from '../config/features';

// Helper function to convert API DTO to frontend Market type
export const convertDTOtoMarket = (dto: MetaMarketDTO, eventId: number): Market => {
    // Map selections if they exist
    const selections: Selection[] = dto.selections ? dto.selections.map(selectionDto => ({
        id: selectionDto.id,
        externalId: selectionDto.id.toString(), // Using ID as externalId if not provided
        name: selectionDto.name,
        reference: selectionDto.reference,
        status: selectionDto.status as SelectionStatus,
        outcome: selectionDto.outcome as SelectionOutcome,
        marketId: dto.id,
        createdAt: selectionDto.createdAt,
        updatedAt: selectionDto.updatedAt,
        prices: selectionDto.prices ? selectionDto.prices.map(priceDto => ({
            id: priceDto.id,
            side: priceDto.side as Side,
            price: priceDto.price,
            probability: priceDto.probability,
            selectionId: selectionDto.id,
            createdAt: priceDto.createdAt,
            updatedAt: priceDto.updatedAt,
            priceChange: null // Initialize with no price change
        })) : []
    })) : [];

    // Convert market type from backend format to frontend enum
    const convertMarketType = (type: string): MarketType => {
        // Log the received market type for debugging
        console.log('Backend market type:', type);
        
        switch(type) {
            case 'TWO_WAY_MATCH_ODDS':
                return MarketType.TWO_WAY_MATCH_ODDS;
            case '_2_WAY_MATCH_ODDS':
                return MarketType.TWO_WAY_MATCH_ODDS;
            case 'THREE_WAY_MATCH_ODDS':
                return MarketType.THREE_WAY_MATCH_ODDS;
            case '_3_WAY_MATCH_ODDS':
                return MarketType.THREE_WAY_MATCH_ODDS;
            case 'INNING_RUNS':
                return MarketType.INNING_RUNS;
            case 'NEXT_BALL':
                return MarketType.NEXT_BALL;
            case 'TOSS':
                return MarketType.TOSS;
            case 'TEAM_4S':
                return MarketType.TEAM_4S;
            case 'TEAM_6S':
                return MarketType.TEAM_6S;
            case 'TOTAL_4S':
                return MarketType.TOTAL_4S;
            case 'TOTAL_6S':
                return MarketType.TOTAL_6S;
            case 'MATCH_TOTAL_RUNS':
                return MarketType.MATCH_TOTAL_RUNS;
            case 'MATCH_TOTAL_WICKETS':
                return MarketType.MATCH_TOTAL_WICKETS;
            case 'TEAM_ODD_EVEN':
                return MarketType.TEAM_ODD_EVEN;
            case 'MATCH_ODD_EVEN':
                return MarketType.MATCH_ODD_EVEN;
            case 'TT_MATCH_WINNER':
                return MarketType.TT_MATCH_WINNER;
            case 'TT_MATCH_TOTAL_ODD_EVEN':
                return MarketType.TT_MATCH_TOTAL_ODD_EVEN;
            case 'TT_SET_WINNER':
                return MarketType.TT_SET_WINNER;
            case 'TT_MATCH_SETS_HANDICAP':
                return MarketType.TT_MATCH_SETS_HANDICAP;
            case 'TT_RACE_TO_N':
                return MarketType.TT_RACE_TO_N;
            case 'TT_SET_POINTS_HANDICAP':
                return MarketType.TT_SET_POINTS_HANDICAP;
            case 'TT_SET_TOTAL_POINTS':
                return MarketType.TT_SET_TOTAL_POINTS;
            case 'TT_MATCH_TOTAL_POINTS':
                return MarketType.TT_MATCH_TOTAL_POINTS;
            case 'TT_SET_TOTAL_ODD_EVEN':
                return MarketType.TT_SET_TOTAL_ODD_EVEN;
            case 'TT_MATCH_CORRECT_SCORE':
                return MarketType.TT_MATCH_CORRECT_SCORE;
            case 'TT_SET_POINT_N_WINNER':
                return MarketType.TT_SET_POINT_N_WINNER;
            case 'TENNIS_MATCH_WINNER':
                return MarketType.TENNIS_MATCH_WINNER;
            case 'TENNIS_MATCH_TOTAL_ODD_EVEN':
                return MarketType.TENNIS_MATCH_TOTAL_ODD_EVEN;
            case 'TENNIS_MATCH_TOTAL_GAMES':
                return MarketType.TENNIS_MATCH_TOTAL_GAMES;
            case 'TENNIS_MATCH_GAMES_HANDICAP':
                return MarketType.TENNIS_MATCH_GAMES_HANDICAP;
            case 'TENNIS_MATCH_RACE_TO_N':
                return MarketType.TENNIS_MATCH_RACE_TO_N;
            case 'TENNIS_SET_GAME_WINNER':
                return MarketType.TENNIS_SET_GAME_WINNER;
            default:
                // Not match odds: a market rendered as the wrong type reads as real, and its own card
                // would never be missed. Unmapped markets get their own section instead.
                console.warn(`Unknown market type: ${type}, showing it as unmapped`);
                return MarketType.UNKNOWN;
        }
    };
    
    return {
        id: dto.id,
        eventId: dto.eventId,
        name: dto.name,
        marketType: convertMarketType(dto.marketType),
        timestamp: Date.now(),
        marketStatus: dto.marketStatus as MarketStatus,
        marketOutcome: dto.marketOutcome as MarketOutcome,
        reference: dto.reference,
        selections,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
        closedAt: dto.closedAt,
        probabilitiesHistory: dto.probabilitiesHistory ? dto.probabilitiesHistory.map(history => ({
            probabilities: history.probabilities,
            createdAt: history.createdAt
        })) : undefined,
        gr8MarketId: dto.gr8MarketId,
        gr8PeriodId: dto.gr8PeriodId
    };
};

export const marketService = {
    getMarketsByEventId: async (eventId: number): Promise<Market[]> => {
        try {
            console.log(`Fetching markets for event ID: ${eventId}`);
            console.log(`Using endpoint: ${ENDPOINTS.MARKETS_BY_EVENT(eventId)}`);
            
            // Use the new MarketController endpoint
            const dtos = await apiService.get<MetaMarketDTO[]>(ENDPOINTS.MARKETS_BY_EVENT(eventId));
            
            console.log('Markets API response:', dtos);
            
            if (!dtos || dtos.length === 0) {
                console.log('No markets returned from API');
                return [];
            }
            
            const markets = dtos.map(dto => convertDTOtoMarket(dto, eventId));
            console.log('Converted markets:', markets);
            
            return markets;
        } catch (error) {
            console.error('Failed to fetch markets:', error);
            return [];
        }
    },

    openMarket: async (marketId: number): Promise<Market> => {
        try {
            const dto = await apiService.patch<MetaMarketDTO>(`${ENDPOINTS.MARKETS_BASE}/${marketId}/open`);
            return convertDTOtoMarket(dto, dto.eventId);
        } catch (error) {
            console.error(`Error opening market ${marketId}:`, error);
            throw error;
        }
    },

    unvoidMarket: async (marketId: number): Promise<Market> => {
        try {
            const dto = await apiService.patch<MetaMarketDTO>(`${ENDPOINTS.MARKETS_BASE}/${marketId}/unvoid`);
            return convertDTOtoMarket(dto, dto.eventId);
        } catch (error) {
            console.error(`Error unvoiding market ${marketId}:`, error);
            throw error;
        }
    },

    unsettleMarket: async (marketId: number): Promise<Market> => {
        try {
            const dto = await apiService.patch<MetaMarketDTO>(`${ENDPOINTS.MARKETS_BASE}/${marketId}/unsettle`);
            return convertDTOtoMarket(dto, dto.eventId);
        } catch (error) {
            console.error(`Error unsettling market ${marketId}:`, error);
            throw error;
        }
    },

    settleMarket: async (marketId: number, winningSelectionId: number): Promise<Market> => {
        try {
            const dto = await apiService.patch<MetaMarketDTO>(
                `${ENDPOINTS.MARKETS_BASE}/${marketId}/settle?winningSelectionId=${winningSelectionId}`
            );
            return convertDTOtoMarket(dto, dto.eventId);
        } catch (error) {
            console.error(`Error settling market ${marketId}:`, error);
            throw error;
        }
    },

    voidMarket: async (marketId: number): Promise<Market> => {
        try {
            const dto = await apiService.patch<MetaMarketDTO>(`${ENDPOINTS.MARKETS_BASE}/${marketId}/void`);
            return convertDTOtoMarket(dto, dto.eventId);
        } catch (error) {
            console.error(`Error voiding market ${marketId}:`, error);
            throw error;
        }
    },

    closeMarket: async (marketId: number): Promise<Market> => {
        console.log('marketService.closeMarket called with marketId:', marketId);
        try {
            const dto = await apiService.patch<MetaMarketDTO>(`${ENDPOINTS.MARKETS_BASE}/${marketId}/close`);
            return convertDTOtoMarket(dto, dto.eventId);
        } catch (error) {
            console.error(`Error closing market ${marketId}:`, error);
            throw error;
        }
    },

    closeAllMarkets: async (eventId: number): Promise<Market[]> => {
        try {
            const dtos = await apiService.patch<MetaMarketDTO[]>(`${ENDPOINTS.MARKETS_BASE}/event/${eventId}/close`);
            return dtos.map((dto: MetaMarketDTO) => convertDTOtoMarket(dto, eventId));
        } catch (error) {
            console.error(`Error closing all markets for event ${eventId}:`, error);
            throw error;
        }
    },

    voidEventMarkets: async (eventId: number): Promise<Market[]> => {
        try {
            const dtos = await apiService.patch<MetaMarketDTO[]>(`${ENDPOINTS.MARKETS_BASE}/event/${eventId}/void`);
            return dtos.map((dto: MetaMarketDTO) => convertDTOtoMarket(dto, eventId));
        } catch (error) {
            console.error(`Error voiding all markets for event ${eventId}:`, error);
            throw error;
        }
    }
};
